import React, { useState } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import UserAuthentication from '@/features/auth/components/UserAuthentication';
import { isForgotPasswordLogin } from '@/features/auth/constants/authRoutes';
import { type UserCredentials, authService } from '@/features/auth/services/authService';
import { notificationService } from '@/services/notificationService';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useTranslation } from 'react-i18next';

export default function UserLoginPage() {
  const { t } = useTranslation();
  useDocumentTitle(t('pageTitles.userLoginDocument'));
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // When redirected from deposit flow (no token), state.redirectTo is set so we go back after login.
  // Fallback to sessionStorage in case location.state was lost (e.g. new tab, refresh).
  const loginState = location.state as { redirectTo?: string; orderData?: unknown } | undefined;
  const redirectToAfterLogin =
    loginState?.redirectTo ??
    sessionStorage.getItem('login_redirect_to') ??
    undefined;
  const orderDataFromState = loginState?.orderData;
  const normalizedRedirect =
    redirectToAfterLogin === '/account/finance/payment-checkout'
      ? '/account/finance/deposit'
      : redirectToAfterLogin;

  // Get query params for provider/action if coming from deposit/withdrawal flow
  const providerFromQuery = searchParams.get('provider') || searchParams.get('providerId');
  const providerName = searchParams.get('providerName');
  const action = searchParams.get('action') as 'deposit' | 'withdrawal' | null;
  const totalAmount = parseFloat(searchParams.get('totalAmount') || '0');
  const isWalletFlow = searchParams.get('walletFlow') === 'true';
  const initialForgotMode = isForgotPasswordLogin(searchParams);

  const selectedProvider = providerFromQuery && providerName ? {
    id: providerFromQuery,
    name: providerName,
    tagline: '',
    gradient: '',
    icon: ''
  } : null;

  const handleBack = () => {
    if (selectedProvider && action) {
      // Deposit reads ?provider=; game withdraw lives on Games (legacy /withdrawals redirects there).
      if (action === 'deposit') {
        navigate(
          `/account/finance/deposit?provider=${encodeURIComponent(selectedProvider.id)}`
        );
      } else {
        navigate('/account/games');
      }
    } else {
      // Go back to account hub (e.g. when coming from deposit flow without provider in URL)
      navigate('/account/settings');
    }
  };

  const handleAuthenticate = async (credentials: UserCredentials) => {
    try {
      const me = await authService.getCurrentUser();
      const meUser = (me.data as any)?.user;
      const mustChangePassword = Boolean(meUser?.profile?.mustChangePassword);
      if (mustChangePassword) {
        notificationService.success(t('auth.mustChangePasswordNotice'), { duration: 2500 });
        navigate('/account/security', { replace: true });
        return;
      }
      // Clear stored redirect so it's not reused on next login
      sessionStorage.removeItem('login_redirect_to');

      // Navigate immediately so no other logic can override; use replace to avoid back-stack
      const goTo = (path: string, state?: unknown) => {
        notificationService.success(t('auth.loginSuccessRedirecting'), { duration: 2000 });
        navigate(path, { replace: true, ...(state !== undefined && state !== null ? { state } : {}) });
      };

      // After successful login: if in deposit flow (has money), go to deposit page (wallet-only).
      const isDepositFlow =
        normalizedRedirect === '/account/finance/deposit' ||
        (action === 'deposit' && selectedProvider && totalAmount > 0) ||
        (totalAmount > 0 && !isWalletFlow);
      if (isDepositFlow) {
        const depositPath = selectedProvider
          ? `/account/finance/deposit?provider=${encodeURIComponent(selectedProvider.id)}`
          : '/account/finance/deposit';
        goTo(depositPath, orderDataFromState ?? undefined);
      } else if (isWalletFlow && !normalizedRedirect) {
        goTo('/account/settings');
      } else {
        goTo(normalizedRedirect || '/account/settings');
      }
    } catch (error) {
      console.error('Authentication error:', error);
    }
  };

  const handleRegister = () => {
    // Navigate to registration page if needed
    navigate('/register');
  };

  return (
    <UserAuthentication
      selectedProvider={selectedProvider}
      selectedAction={action}
      onBack={handleBack}
      onAuthenticate={handleAuthenticate}
      onRegister={handleRegister}
      totalAmount={totalAmount}
      isWalletFlow={isWalletFlow}
      redirectToAfterLogin={redirectToAfterLogin}
      hideHeader={true}
      initialForgotMode={initialForgotMode}
    />
  );
}
